import type { ArrivalTone } from './arrivalAnnounce';

/**
 * The three arrival tones, synthesised with the Web Audio API.
 *
 * <b>Why synthesised and not audio files.</b> Three ~200ms cues would be three network assets to
 * ship, cache-bust and serve — for roughly 40 lines of oscillator. Synthesis also keeps them
 * consistent: one envelope, one gain ceiling, so "error" cannot end up four times louder than
 * "success" because someone exported it hotter.
 *
 * <b>They are deliberately quiet and short.</b> Peak gain 0.07 of full scale, under 300ms. This
 * fires while someone is working in another window; it has to be noticeable without being an event.
 */

/** Loudest point of the envelope, as a fraction of full scale. Deliberately low. */
const PEAK_GAIN = 0.07;

/**
 * The shortest gap between two audible cues. A batch frame can carry a dozen transitions and the
 * SSE stream can deliver several frames in a second; without this, "several postings settled" is a
 * machine-gun. The visual ripple has no such limit — it is re-triggered, not stacked.
 */
const MIN_GAP_MS = 1200;

/** frequency in Hz, when it starts (seconds from now), how long it rings. */
type Note = { hz: number; at: number; for: number };

/**
 * ⚠️ Direction carries the meaning, not pitch alone — rising reads as resolved, falling as wrong,
 * across cultures and for anyone who cannot see the ripple. A listener should be able to tell these
 * apart from the next room without counting notes.
 */
const TONES: Record<ArrivalTone, { notes: readonly Note[]; wave: OscillatorType; gain: number }> = {
  // Two rising notes, soft. "Done."
  success: {
    notes: [
      { hz: 659.25, at: 0, for: 0.09 },
      { hz: 987.77, at: 0.085, for: 0.16 },
    ],
    wave: 'triangle',
    gain: 1,
  },
  // Two falling notes, lower and a touch louder. "Look at this."
  alert: {
    notes: [
      { hz: 466.16, at: 0, for: 0.13 },
      { hz: 311.13, at: 0.125, for: 0.22 },
    ],
    wave: 'triangle',
    gain: 1.25,
  },
  // One neutral note. "Something arrived."
  info: {
    notes: [{ hz: 783.99, at: 0, for: 0.13 }],
    wave: 'sine',
    gain: 0.85,
  },
};

let context: AudioContext | null = null;
let lastPlayedAt = 0;

/**
 * ⚠️ Created LAZILY, on the first tone rather than at import.
 *
 * Browsers block audio until the page has been interacted with, and a context constructed before
 * that starts `suspended`. Building it at import would also mean every page load spins up an audio
 * device for a user who may never receive a notification.
 */
function getContext(): AudioContext | null {
  if (context) return context;

  // Guarded rather than assumed: this module is imported by a hook that vitest can reach (node, no
  // Web Audio), and Safari historically shipped this prefixed.
  const Ctor =
    typeof window === 'undefined'
      ? undefined
      : (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);

  if (!Ctor) return null;

  try {
    context = new Ctor();
    return context;
  } catch {
    return null;
  }
}

/**
 * Play one arrival tone. Silent — never throwing — when it cannot.
 *
 * ⚠️ THE AUTOPLAY POLICY IS NOT AN ERROR CASE. A tab that has loaded but not yet been clicked in
 * cannot make sound, by design, in every current browser. `resume()` is attempted (it succeeds once
 * the user has interacted at all, which in this app means the moment they have clicked anything),
 * and if it does not, the ripple carries the message alone. This must NEVER surface a toast or a
 * console error: the user did nothing wrong and there is nothing for them to fix.
 */
export function playArrivalTone(tone: ArrivalTone): void {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_GAP_MS) return;

  const ctx = getContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    // Fire-and-forget: if the gesture requirement has not been met this rejects, and the tone is
    // simply skipped. Re-attempted on the next arrival, by which point they have probably clicked.
    void ctx.resume().catch(() => {});
    if (ctx.state === 'suspended') return;
  }

  const recipe = TONES[tone];

  try {
    for (const note of recipe.notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const startAt = ctx.currentTime + note.at;
      const peak = PEAK_GAIN * recipe.gain;

      osc.type = recipe.wave;
      osc.frequency.setValueAtTime(note.hz, startAt);

      // ⚠️ RAMPED, not switched. Stepping gain from 0 to peak puts a discontinuity in the waveform,
      // which is audible as a click at the top of every note — the thing that makes synthesised UI
      // sound cheap. 12ms in, exponential out.
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + note.for);

      osc.connect(gain).connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + note.for + 0.02);
    }

    lastPlayedAt = now;
  } catch {
    /* An audio device that went away mid-session is not worth a word to the user. */
  }
}

/** Test seam — the rate limiter is module state, and a suite that plays twice would measure it. */
export function resetArrivalToneThrottleForTests(): void {
  lastPlayedAt = 0;
}
